import { Box, InputAdornment, MenuItem, Paper, Select } from "@mui/material";
import Image from "next/image";
import { t } from "@lingui/macro";
import { useEffect, useState } from "react";
import { useLingui } from "@lingui/react";

interface Country {
  code: string;
  name: string;
  flag: string;
}

interface CountrySelectorProps {
  countryCode: string;
  setCountryCode: (code: string) => void;
  dimensions: {
    width: number;
    top: number;
    left: number;
  };
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
  countryCode,
  setCountryCode,
  dimensions,
}) => {
  const { i18n } = useLingui();
  const countryList = [
    { code: "41", name: t(i18n)`Switzerland`, flag: "/ch-flag.png" },
    { code: "3", name: t(i18n)`Germany`, flag: "/de-flag.png" },
  ];
  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>(countryList[0]);

  useEffect(() => {
    const selectedCountryTemp = countryList.find((country) => country.code === countryCode);
    setSelectedCountry(selectedCountryTemp || countryList[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  return (
    <InputAdornment
      position="start"
      sx={{
        width: { xs: "130px", md: "115px" },
        bgcolor: "rgba(0,0,0,0.05)",
        height: "100%",
        padding: "0 10px",
        display: "flex",
        alignItems: "center",
        maxHeight: "100%",
        borderRadius: "8px 0 0 8px",
      }}
    >
      <Paper
        sx={{
          position: "absolute",
          top: `0px`,
          left: `0px`,
          width: `90px`,
          height: "50px",
          borderRadius: "8px",
          display: "flex",
          bgcolor: "transparent",
          boxShadow: "none",
        }}
      >
        <Select
          value={countryCode}
          onChange={(event) => setCountryCode(event.target.value)}
          fullWidth
          variant="standard"
          disableUnderline
          tabIndex={-1}
          renderValue={() => (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Image
                loading="lazy"
                src={selectedCountry?.flag || "/ch-flag.png"}
                alt={t`Flag of ${selectedCountry?.name}` || t`unknown`}
                width={25}
                height={25}
              />
              <Box sx={{ ml: "8px" }}>+{selectedCountry?.code}</Box>
            </Box>
          )}
          sx={{
            ml: "5px",
            fontSize: "inherit",
            width: "inherit",
            "& .MuiSelect-select": {
              paddingRight: "5px !important",
            },
            "& .MuiSelect-select .Mui-focused": {
              backgroundColor: "transparent",
            },
            "& .MuiSelect-select .Mui-focusVisible": {
              backgroundColor: "transparent",
            },
          }}
          MenuProps={{
            disablePortal: true,
            PaperProps: {
              sx: {
                position: "absolute",
                left: `${dimensions.left}px !important`,
                width: `${dimensions.width}px`,
                zIndex: 9999,
              },
            },
          }}
        >
          {countryList.map((country) => (
            <MenuItem
              aria-label={country.code}
              key={country.code}
              value={country.code}
              sx={{ display: "flex", alignItems: "center", py: 1, borderBottom: "none" }}
            >
              <Image
                loading="lazy"
                src={country.flag}
                alt={t`Flag of ${country.name}`}
                width={20}
                height={20}
              />
              <Box sx={{ ml: "8px" }}>
                {country.name} <span>+{country.code}</span>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </Paper>
    </InputAdornment>
  );
};

export default CountrySelector;
